import {
  Component,
  ViewEncapsulation,
  Input,
  ViewChild,
  AfterViewInit,
  Output,
  EventEmitter,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DecimalPipe } from '@angular/common';
import { fromEvent } from 'rxjs';

import { FilterItem } from '../../../../models/filter-item.model';
import { PriceRangeEvent } from '../../../../models/price-range.event';
import { formatToNumber } from '../../../../shared/helpers';

type HTMLInputRangeElement = HTMLInputElement & {
  value: number | string;
  min: number;
  max: number;
};

const color = {
  sliderColor: '#C6C6C6',
  rangeColor: '#2B4164',
};

@UntilDestroy()
@Component({
  selector: 'price-range-slider',
  templateUrl: './price-range-slider.component.html',
  styleUrls: ['./price-range-slider.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [DecimalPipe],
  standalone: false,
})
export class PriceRangeSliderComponent implements AfterViewInit {
  @Input() rangeValue: FilterItem;

  @Output() updated = new EventEmitter<PriceRangeEvent>();

  @ViewChild('minInput') minInput: {
    nativeElement: HTMLInputRangeElement;
  };
  @ViewChild('maxInput') maxInput: {
    nativeElement: HTMLInputRangeElement;
  };
  @ViewChild('minSlider') minSlider: {
    nativeElement: HTMLInputRangeElement;
  };
  @ViewChild('maxSlider') maxSlider: {
    nativeElement: HTMLInputRangeElement;
  };

  minInputOldValue = '';
  maxInputOldValue = '';

  constructor(private decimalPipe: DecimalPipe) {}

  ngAfterViewInit(): void {
    if (this.maxSlider) {
      this.rangeValue.inputMinPrice = this.formatFromNumber(
        this.rangeValue.min_price
      );
      this.rangeValue.inputMaxPrice = this.formatFromNumber(
        this.rangeValue.max_price
      );
      this.minInputOldValue = this.rangeValue.inputMinPrice;
      this.maxInputOldValue = this.rangeValue.inputMaxPrice;

      this.minSlider.nativeElement.value = this.rangeValue.min_price as string;
      this.maxSlider.nativeElement.value = this.rangeValue.max_price as string;

      this.fillSlider(
        this.minSlider.nativeElement,
        this.maxSlider.nativeElement,
        this.maxSlider.nativeElement
      );

      this.setToggleAccessible(this.maxSlider.nativeElement);

      this.restrictTypeNumberOutsideOfRange();
    }
  }

  onMinInputChange(): void {
    this.syncMinSliderWithInput();
  }

  onMaxInputChange(): void {
    this.syncMaxSliderWithInput();
  }

  onMinSliderInput(event: Event | any): void {
    const [from, to] = this.getParsed(
      this.minSlider.nativeElement,
      this.maxSlider.nativeElement
    );

    if (from > to) {
      event.stopPropagation();
      this.minSlider.nativeElement.value = to.toString();
      this.minInput.nativeElement.value = this.formatFromNumber(to);
    } else {
      this.fillSlider(
        this.minSlider.nativeElement,
        this.maxSlider.nativeElement,
        this.maxSlider.nativeElement
      );

      this.minInput.nativeElement.value = this.formatFromNumber(from);

      this.rangeValue.inputMinPrice = this.formatFromNumber(from);
      this.rangeValue.inputMaxPrice = this.formatFromNumber(to);
      this.rangeValue.selected = true;

      this.updated.emit({ minPrice: from, maxPrice: to });
    }
  }

  onMaxSliderInput(event: Event | any): void {
    const [from, to] = this.getParsed(
      this.minSlider.nativeElement,
      this.maxSlider.nativeElement
    );

    if (from > to) {
      event.stopPropagation();
      this.maxInput.nativeElement.value = this.formatFromNumber(from);
      this.maxSlider.nativeElement.value = from.toString();
    } else {
      this.fillSlider(
        this.minSlider.nativeElement,
        this.maxSlider.nativeElement,
        this.maxSlider.nativeElement
      );

      this.setToggleAccessible(this.maxSlider.nativeElement);

      this.maxSlider.nativeElement.value = to.toString();
      this.maxInput.nativeElement.value = this.formatFromNumber(to);

      this.rangeValue.inputMinPrice = this.formatFromNumber(from);
      this.rangeValue.inputMaxPrice = this.formatFromNumber(to);

      this.rangeValue.selected = true;

      this.updated.emit({ minPrice: from, maxPrice: to });
    }
  }

  private syncMinSliderWithInput(): void {
    const [from, to] = this.getParsed(
      this.minInput.nativeElement,
      this.maxInput.nativeElement
    );

    if (from > to) {
      this.minSlider.nativeElement.value = to.toString();
      this.minInput.nativeElement.value = this.formatFromNumber(to);
    } else {
      this.minSlider.nativeElement.value = from.toString();
    }

    this.fillSlider(
      this.minInput.nativeElement,
      this.maxInput.nativeElement,
      this.maxSlider.nativeElement
    );

    this.rangeValue.inputMinPrice = this.formatFromNumber(from);
    this.rangeValue.inputMaxPrice = this.formatFromNumber(to);

    this.rangeValue.selected = true;

    this.updated.emit({ minPrice: from, maxPrice: to });
  }

  private syncMaxSliderWithInput(): void {
    const [from, to] = this.getParsed(
      this.minInput.nativeElement,
      this.maxInput.nativeElement
    );

    if (from <= to) {
      this.maxSlider.nativeElement.value = to.toString();
      this.maxInput.nativeElement.value = this.formatFromNumber(to);
    } else {
      this.maxSlider.nativeElement.value = from.toString();
    }

    this.fillSlider(
      this.minInput.nativeElement,
      this.maxInput.nativeElement,
      this.maxSlider.nativeElement
    );

    this.setToggleAccessible(this.maxInput.nativeElement);

    this.rangeValue.inputMinPrice = this.formatFromNumber(from);
    this.rangeValue.inputMaxPrice = this.formatFromNumber(to);

    this.rangeValue.selected = true;

    this.updated.emit({ minPrice: from, maxPrice: to });
  }

  private getParsed(
    currentFrom: HTMLInputRangeElement | HTMLInputElement,
    currentTo: HTMLInputRangeElement | HTMLInputElement
  ): [number, number] {
    return [formatToNumber(currentFrom.value), formatToNumber(currentTo.value)];
  }

  private fillSlider(
    from: HTMLInputRangeElement,
    to: HTMLInputRangeElement,
    controlSlider: HTMLInputRangeElement
  ): void {
    const rangeDistance = to.max - to.min;
    const fromPosition = formatToNumber(from.value) - to.min;
    const toPosition = formatToNumber(to.value) - to.min;

    if (fromPosition > toPosition) {
      return;
    }

    controlSlider.style.background = `linear-gradient(to right,
      ${color.sliderColor} 0%,
      ${color.sliderColor} ${(fromPosition / rangeDistance) * 100}%,
      ${color.rangeColor} ${(fromPosition / rangeDistance) * 100}%,
      ${color.rangeColor} ${(toPosition / rangeDistance) * 100}%, 
      ${color.sliderColor} ${(toPosition / rangeDistance) * 100}%, 
      ${color.sliderColor} 100%)`;
  }

  private setToggleAccessible(currentTarget: HTMLInputRangeElement): void {
    const maxSlider = this.maxSlider.nativeElement;
    if (Number(currentTarget.value) <= 0) {
      maxSlider.style.zIndex = '2';
    } else {
      maxSlider.style.zIndex = '0';
    }
  }

  private restrictTypeNumberOutsideOfRange(): void {
    fromEvent(this.minInput.nativeElement, 'input')
      .pipe(untilDestroyed(this))
      .subscribe((event: Event | any) => {
        this.onlyNumberForInputField('min', event);
      });

    fromEvent(this.maxInput.nativeElement, 'input')
      .pipe(untilDestroyed(this))
      .subscribe((event: Event | any) => {
        this.onlyNumberForInputField('max', event);
      });

    fromEvent(this.minInput.nativeElement, 'focusout')
      .pipe(untilDestroyed(this))
      .subscribe((event: Event | any) => {
        if (Number(event.target.value) < Number(this.rangeValue.minPrice)) {
          this.minInput.nativeElement.value = this.formatFromNumber(
            this.rangeValue.minPrice
          );
          this.minSlider.nativeElement.value =
            this.rangeValue.minPrice.toString();
        } else if (
          Number(event.target.value) > Number(this.maxInput.nativeElement.value)
        ) {
          this.minInput.nativeElement.value = this.formatFromNumber(
            this.maxInput.nativeElement.value
          );
          this.minSlider.nativeElement.value =
            this.maxInput.nativeElement.value;
        }

        this.syncMinSliderWithInput();
      });

    fromEvent(this.maxInput.nativeElement, 'focusout')
      .pipe(untilDestroyed(this))
      .subscribe((event: Event | any) => {
        if (Number(event.target.value) > Number(this.rangeValue.maxPrice)) {
          this.maxInput.nativeElement.value = this.formatFromNumber(
            this.rangeValue.maxPrice
          );
          this.maxSlider.nativeElement.value =
            this.rangeValue.maxPrice.toString();
        } else if (
          Number(event.target.value) < Number(this.minInput.nativeElement.value)
        ) {
          this.maxInput.nativeElement.value = this.formatFromNumber(
            this.minInput.nativeElement.value
          );
          this.maxSlider.nativeElement.value =
            this.minInput.nativeElement.value;
        }

        this.syncMaxSliderWithInput();
      });
  }

  private formatFromNumber(value: number | string): string {
    return this.decimalPipe.transform(value);
  }

  private onlyNumberForInputField(elem: string, event: Event | any): void {
    const onlyNumber = /^\d*\.?\d*$/;
    const value = event.target.value.replace(/\,/g, '');
    if (!onlyNumber.test(value)) {
      event.target.value =
        elem === 'min' ? this.minInputOldValue : this.maxInputOldValue;
    } else {
      if (elem === 'min') {
        this.minInputOldValue = value;
      } else {
        this.maxInputOldValue = value;
      }
    }
  }
}
